(function () {
  'use strict';

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var DRAG_THRESHOLD = 6; // px — below this, treat the pointer sequence as a click, not a drag

  function initEntranceAnimation(root) {
    var targets = root.querySelectorAll('.eb-anim');
    if (!targets.length) return;

    if (prefersReducedMotion || !('IntersectionObserver' in window)) {
      targets.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );
    targets.forEach(function (el) { observer.observe(el); });
  }

  function initCarousel(section) {
    var wrap = section.querySelector('.eb-op-track-wrap');
    var track = section.querySelector('.eb-op-track');
    if (!wrap || !track) return;

    var autoscroll = section.dataset.autoscroll === 'true';
    var duration = parseFloat(section.dataset.autoscrollSpeed) || 70; // seconds for full loop — slow & steady
    var prevBtn = section.querySelector('.eb-op-arrow--prev');
    var nextBtn = section.querySelector('.eb-op-arrow--next');

    var isPointerDown = false;   // pointer is currently down (may or may not become a drag)
    var isDragging = false;      // movement exceeded threshold — treat as drag, suppress click
    var dragStartX = 0;
    var dragStartOffset = 0;
    var activePointerId = null;
    var offset = 0; // current translateX in px (negative moves left)
    var paused = false;
    var rafId = null;
    var lastTs = null;
    var loopWidth = 0; // width of one full set of cards (half of track, when duplicated)

    function measure() {
      var next = track.scrollWidth / (autoscroll ? 2 : 1);
      // Ignore transient zero/invalid readings (can happen mid-resize in the
      // theme editor while the sidebar animates) — keep the last good value
      // instead of stalling the loop permanently.
      if (next > 0) loopWidth = next;
    }

    function setTransform() {
      track.style.transform = 'translateX(' + offset + 'px)';
    }

    function wrapOffset() {
      if (!autoscroll || loopWidth <= 0) return;
      if (offset <= -loopWidth) offset += loopWidth;
      if (offset > 0) offset -= loopWidth;
    }

    function tick(ts) {
      if (!paused && autoscroll && !isDragging) {
        if (lastTs == null) lastTs = ts;
        var dt = (ts - lastTs) / 1000;
        lastTs = ts;
        var pxPerSecond = loopWidth / duration;
        offset -= pxPerSecond * dt;
        wrapOffset();
        setTransform();
      } else {
        lastTs = ts;
      }
      rafId = requestAnimationFrame(tick);
    }

    function cardStep() {
      var card = track.querySelector('.eb-op-card');
      if (!card) return 300;
      var style = getComputedStyle(track);
      var gap = parseFloat(style.columnGap || style.gap || 0);
      return card.getBoundingClientRect().width + gap;
    }

    function nudge(direction) {
      paused = true;
      offset += direction * cardStep() * -1;
      if (!autoscroll) {
        var maxOffset = 0;
        var minOffset = -(track.scrollWidth - wrap.clientWidth);
        if (offset > maxOffset) offset = maxOffset;
        if (offset < minOffset) offset = minOffset;
      } else {
        wrapOffset();
      }
      track.style.transition = 'transform 0.4s ease';
      setTransform();
      window.setTimeout(function () {
        track.style.transition = '';
        paused = false;
      }, 450);
    }

    // Suppress the click that follows a real drag, so a swipe doesn't
    // accidentally navigate. Only attached/used once an actual drag happens.
    function suppressNextClick(e) {
      e.preventDefault();
      e.stopPropagation();
      wrap.removeEventListener('click', suppressNextClick, true);
    }

    if (prefersReducedMotion) {
      // Fall back to native, user-controlled horizontal scroll.
      wrap.style.overflowX = 'auto';
      track.style.transform = 'none';
      if (prevBtn) prevBtn.addEventListener('click', function () { wrap.scrollBy({ left: -cardStep(), behavior: 'smooth' }); });
      if (nextBtn) nextBtn.addEventListener('click', function () { wrap.scrollBy({ left: cardStep(), behavior: 'smooth' }); });
      return;
    }

    measure();
    window.addEventListener('resize', measure);

    if (prevBtn) prevBtn.addEventListener('click', function () { nudge(-1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { nudge(1); });

    section.addEventListener('mouseenter', function () { paused = true; });
    section.addEventListener('mouseleave', function () { paused = false; });
    section.addEventListener('focusin', function () { paused = true; });
    section.addEventListener('focusout', function () { paused = false; });

    // touch / pointer drag support
    wrap.addEventListener('pointerdown', function (e) {
      isPointerDown = true;
      isDragging = false; // don't assume drag yet — wait for real movement
      dragStartX = e.clientX;
      dragStartOffset = offset;
      activePointerId = e.pointerId;
      // NOTE: pointer capture is intentionally NOT set here yet.
      // Capturing immediately on every click retargets pointerup away from
      // the clicked link and breaks its click event in several browsers.
      // We only capture once we've confirmed the user is actually dragging.
    });

    wrap.addEventListener('pointermove', function (e) {
      if (!isPointerDown) return;

      var deltaX = e.clientX - dragStartX;

      if (!isDragging) {
        if (Math.abs(deltaX) < DRAG_THRESHOLD) return; // still just a click/tap
        // Movement crossed the threshold — this is now a real drag.
        isDragging = true;
        paused = true;
        if (activePointerId != null) {
          wrap.setPointerCapture(activePointerId);
        }
        // A click event will still fire on pointerup after a drag; swallow it.
        wrap.addEventListener('click', suppressNextClick, true);
      }

      offset = dragStartOffset + deltaX;
      if (!autoscroll) {
        var maxOffset = 0;
        var minOffset = -(track.scrollWidth - wrap.clientWidth);
        if (offset > maxOffset) offset = maxOffset;
        if (offset < minOffset) offset = minOffset;
      } else {
        wrapOffset();
      }
      setTransform();
    });

    function endDrag(e) {
      if (!isPointerDown) return;
      isPointerDown = false;
      if (isDragging && activePointerId != null) {
        try { wrap.releasePointerCapture(activePointerId); } catch (err) { /* no-op */ }
      }
      isDragging = false;
      paused = false;
      activePointerId = null;
    }
    wrap.addEventListener('pointerup', endDrag);
    wrap.addEventListener('pointercancel', endDrag);

    // Safety net: if the pointer is released (or the tab loses focus) outside
    // the carousel bounds, the listeners above may never fire. Without this,
    // "paused" can get stuck true and the loop looks like it "runs a bit
    // then stops for good." Catch it at the window level too.
    window.addEventListener('pointerup', endDrag);
    window.addEventListener('pointercancel', endDrag);
    window.addEventListener('blur', endDrag);

    rafId = requestAnimationFrame(tick);
  }

  function init(context) {
    var sections = (context || document).querySelectorAll('.eb-our-products');
    sections.forEach(function (section) {
      initCarousel(section);
      initEntranceAnimation(section);
    });
  }

  document.addEventListener('DOMContentLoaded', function () { init(document); });

  // Shopify theme editor support: re-init when this section is added/reloaded
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();